import { useCallback } from 'react';
import { db } from '../sync/db';
import { useAuthStore } from './authStore';
import { getCompanyRoleHomeRoute } from './companyRole';
import { useMembershipStore, selectActiveMembership } from './membershipStore';
import type { Membership } from './membershipTypes';
import { isLocalDemoAuthEnabled } from './supabaseClient';

async function clearBusinessData(): Promise<void> {
  const tables: Array<{ clear(): Promise<void> }> = [
    db.orders,
    db.outbox,
    db.visit_sessions,
    db.checklist_results,
    db.photos,
    db.signatures,
  ];

  try {
    await Promise.all(tables.map((table) => table.clear()));
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[useSwitchMembership] clearBusinessData error — some stale data may survive', err);
    }
  }
}

export function useSwitchMembership() {
  const memberships = useMembershipStore((state) => state.memberships);
  const switching = useMembershipStore((state) => state.switching);
  const setSwitching = useMembershipStore((state) => state.setSwitching);
  const setActiveMembership = useMembershipStore((state) => state.setActiveMembership);
  const activeMembership = useMembershipStore(selectActiveMembership);

  const switchMembership = useCallback(
    async (target: Membership) => {
      if (switching) return;
      if (target.membership_id === activeMembership?.membership_id) return;

      setSwitching(true);

      try {
        await clearBusinessData();

        useAuthStore.setState((state) => {
          const nextUser = state.user
            ? {
                ...state.user,
                company_id: target.company_id,
                company_membership_id: target.membership_id,
                company_role: target.role,
                role: target.role,
              }
            : state.user;

          if (isLocalDemoAuthEnabled && nextUser) {
            localStorage.setItem('fieldsnap_local_user', JSON.stringify(nextUser));
          }

          return { user: nextUser };
        });

        setActiveMembership(target.membership_id);
        // Hard-redirect (instead of navigate replace) to wipe the back-
        // stack from the previous membership. Otherwise pressing Back
        // can land the user on a route they no longer have access to —
        // or worse, the same route under a different membership where
        // RLS pulls different data and produces confusing mixed state.
        window.location.replace(getCompanyRoleHomeRoute(target.role));
      } finally {
        setSwitching(false);
      }
    },
    [activeMembership?.membership_id, setActiveMembership, setSwitching, switching],
  );

  return {
    memberships,
    activeMembership,
    switching,
    switchMembership,
  };
}
